import {
  learnCardPresentationKey,
  type LearnCardStudyItem,
} from "@-/dev.improve.haniverse.lib.learn.card-action";

import type { FlashcardQueueEntry, LearnDeckState } from "#learn-deck-state";

/**
 * How many flashcards the queue holds after the one on show: the card underneath it, and one more
 * whose front is preloaded so a quick second swipe finds it ready.
 */
export const FLASHCARD_QUEUE_LOOKAHEAD = 2;

/** The next flashcard: the one under the current card, which the current card's action promotes. */
export function flashcardQueueSuccessor(state: LearnDeckState): FlashcardQueueEntry | null {
  return state.queue[0] ?? null;
}

/**
 * Whether `entry` can be uncovered without a blank frame: a card once its front has reported (the
 * face it lands on; its back keeps preloading and a flip before it has rendered draws its loading
 * state), the end of the queue at once (the summary owns its loading, and leaving needs nothing).
 */
export function isFlashcardQueueEntryReady(entry: FlashcardQueueEntry | null): boolean {
  if (entry === null) {
    return false;
  }
  return entry.kind === "end" || entry.slot.ready.front;
}

/**
 * The presentation the next flashcard would be queued after, or `null` when the queue takes no
 * more: it is full, or it ends (nothing follows the end of the flow). A resolution names the tail
 * it was resolved after, and is enqueued only if that is still the tail.
 */
export function flashcardQueueTailKey(state: LearnDeckState): string | null {
  if (state.queue.length >= FLASHCARD_QUEUE_LOOKAHEAD) {
    return null;
  }
  const tail = state.queue.at(-1);
  if (tail === undefined) {
    return learnCardPresentationKey(state.current.presentation);
  }
  return tail.kind === "card" ? learnCardPresentationKey(tail.slot.presentation) : null;
}

/**
 * The next flashcard to resolve: the card at the tail (`origin`), the cards shown before it which
 * the resolution must count as done (`predecessors`, oldest first), and the tail's presentation
 * (`afterKey`). `null` when the queue takes no more.
 */
export function flashcardQueueExtension(state: LearnDeckState): {
  afterKey: string;
  origin: LearnCardStudyItem;
  predecessors: LearnCardStudyItem[];
} | null {
  const afterKey = flashcardQueueTailKey(state);
  if (afterKey === null) {
    return null;
  }
  const items = [
    state.current.item,
    ...state.queue.flatMap((entry) => (entry.kind === "card" ? [entry.slot.item] : [])),
  ];
  const origin = items.at(-1) ?? state.current.item;
  return { afterKey, origin, predecessors: items.slice(0, -1) };
}
